/**
 * `<RecsBar>` — a JP trade-list screen's own controls (`docs/plans/jp-recs.md`).
 *
 *     D  W  M   ·   Long  Short
 *
 * In the screener bar's settings slot (`inline`, `docs/plans/screener-bar.md`);
 * a row of its own under the touch shell's bars. The frequency is
 * what the server is asked for; the side is a filter in the browser
 * (`lib/recs.ts`), so Long ↔ Short repaints and asks for nothing.
 *
 * There is no "Previous" box: the screener's date picker answers the same
 * question for every screen (owner, 2026-10-16).
 */

import type { ScreenerRecs } from "@qufolio/model";

import { RECS_DIRECTIONS, RECS_FREQUENCIES, RECS_FREQUENCY_LABEL } from "../lib/recs";

export interface RecsBarProps {
  recs: ScreenerRecs;
  /** Absent for a panel nobody may change, which then shows its state only. */
  onChange?: (recs: ScreenerRecs) => void;
  /** Inside the screener bar's settings slot: no row of its own, no border. */
  inline?: boolean;
}

export function RecsBar({ recs, onChange, inline }: RecsBarProps) {
  const side = recs.direction ?? "long";
  return (
    <div style={inline ? INLINE : BAR} role="toolbar" aria-label="Trade list">
      <span style={SEG} role="group" aria-label="Bar">
        {RECS_FREQUENCIES.map((frequency) => (
          <button
            key={frequency}
            type="button"
            aria-pressed={recs.frequency === frequency}
            disabled={!onChange}
            onClick={() => onChange?.({ ...recs, frequency })}
            style={recs.frequency === frequency ? ON : OFF}
            title={{ d: "Daily bars", w: "Weekly bars", m: "Monthly bars" }[frequency]}
          >
            {RECS_FREQUENCY_LABEL[frequency]}
          </button>
        ))}
      </span>
      <span style={SEG} role="group" aria-label="Side">
        {RECS_DIRECTIONS.map((direction) => (
          <button
            key={direction}
            type="button"
            aria-pressed={side === direction}
            disabled={!onChange}
            onClick={() => onChange?.({ ...recs, direction })}
            style={side === direction ? ON : OFF}
          >
            {direction === "long" ? "Long" : "Short"}
          </button>
        ))}
      </span>
    </div>
  );
}

const BAR: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "4px 8px",
  background: "var(--qf-surface)",
  borderBottom: "1px solid var(--qf-border)",
  flex: "0 0 auto",
};
const INLINE: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" };
const SEG: React.CSSProperties = {
  display: "inline-flex",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  overflow: "hidden",
};
const BUTTON: React.CSSProperties = {
  border: 0,
  padding: "3px 12px",
  font: "inherit",
  fontSize: 12,
  cursor: "pointer",
};
const OFF: React.CSSProperties = { ...BUTTON, background: "transparent", color: "var(--qf-text)" };
const ON: React.CSSProperties = { ...BUTTON, background: "var(--qf-accent-blue)", color: "var(--qf-background)" };
